/**
 * 動くゾウ（場面ごとのポーズ）の定義（PRODUCT_DEV-1201）
 *
 * 素材は PRODUCT_DEV-1201 に添付された 1 枚絵（眼鏡・点の輪・紙ひこうき・チェックの紙）。
 * サービスイラスト（serviceIllustrations.ts）と同じ全身ゾウだが、**動かす小物を別の PNG に分けて**
 * 重ね、小物だけに CSS アニメーションを掛ける（焼き込んだ 1 枚では小物だけを動かせない）。
 * 切り出しは dev/tools/design/cut-elephant-poses.py（README の「動くゾウ」）。
 *
 * 層の PNG はすべて体と同じ大きさ・同じ位置合わせなので、`absolute inset-0` で重ねるだけで合う。
 * 画像は public/assets/illustration/ 配下。CDN が /assets/* を365日キャッシュするため、
 * 差し替えるときは新しいファイル名にする（同名上書き禁止）。
 *
 * 使い分け:
 * - recordDone: 商談の記録ができた（眼鏡で書類を読む）
 * - waiting: 数秒で終わる短い待ち（点の輪が回る）。録音の解析のような長い待ちは ServiceIllustration の analyzing
 * - sent: 送った・提出した（紙ひこうき）
 * - allDone: やることを片づけきった（チェックの紙）
 * - お礼は「はじめての方へ」の花のゾウ（Onboarding/Celebration.tsx の flower）を使う
 */

export type ElephantPose = "recordDone" | "waiting" | "sent" | "allDone";

/**
 * 小物の動き。class は tailwind.config.js の animation 名（`animate-` を付けて使う）。
 * - pips: 「ピッ」の線が中心から飛び出して消える（2.4 秒ごと）
 * - twinkle: キラッが大きくなって少し回る
 * - spin: 点の輪が 1 コマずつ回る（待ちの表示なので「動きを減らす」設定でも止めず、遅くする）
 * - launch: 紙ひこうきが鼻先から出て、左上へ飛んでいって消える
 */
export type ElephantLayerMotion = "pips" | "twinkle" | "spin" | "launch";

export interface ElephantLayer {
  /** public/ 相対パス */
  src: string;
  motion: ElephantLayerMotion;
  /** 動きの中心（絵の幅・高さに対する %）。拡大・回転の軸と、飛び出す起点 */
  origin: { x: number; y: number };
}

export interface ElephantPoseDef {
  /** 小物を抜いた体（public/ 相対パス） */
  body: string;
  /** 画像の実寸（px）。体と層は同じ大きさ。elephantPoses.test.ts が実ファイルと突き合わせる */
  size: { width: number; height: number };
  layers: ElephantLayer[];
  /** 出てくるときにぽんと弾ませるか（待ちの表示は弾ませない） */
  enter: boolean;
  /** 眼鏡のレンズを光らせる位置（絵の px）。眼鏡のゾウだけ */
  glint?: { cx: number; cy: number; r: number };
}

/**
 * origin・glint の値は、元絵（1254px 角）で測った点を cut-elephant-poses.py の切り出し
 * （外接矩形 + 余白 8px・長辺 512px へ縮小）と同じ式で換算したもの。
 */
export const ELEPHANT_POSES: Record<ElephantPose, ElephantPoseDef> = {
  recordDone: {
    body: "assets/illustration/elephant-record-done-body.png",
    size: { width: 512, height: 391 },
    layers: [
      {
        src: "assets/illustration/elephant-record-done-pips.png",
        motion: "pips",
        origin: { x: 15.2, y: 17.2 },
      },
    ],
    enter: true,
    glint: { cx: 178, cy: 142, r: 26 },
  },
  waiting: {
    body: "assets/illustration/elephant-waiting-body.png",
    size: { width: 512, height: 416 },
    layers: [
      {
        src: "assets/illustration/elephant-waiting-dots.png",
        motion: "spin",
        origin: { x: 11.6, y: 14.7 },
      },
    ],
    enter: false,
  },
  sent: {
    body: "assets/illustration/elephant-sent-body.png",
    size: { width: 512, height: 371 },
    layers: [
      {
        src: "assets/illustration/elephant-sent-plane.png",
        motion: "launch",
        origin: { x: 17.4, y: 22.4 },
      },
    ],
    enter: true,
  },
  allDone: {
    body: "assets/illustration/elephant-all-done-body.png",
    size: { width: 512, height: 398 },
    layers: [
      {
        src: "assets/illustration/elephant-all-done-sparkle.png",
        motion: "twinkle",
        origin: { x: 4.4, y: 9.8 },
      },
      {
        src: "assets/illustration/elephant-all-done-pips.png",
        motion: "pips",
        origin: { x: 23.4, y: 15.6 },
      },
    ],
    enter: true,
  },
};

/** public/ 相対パスに BASE_URL を付ける */
export const elephantAssetSrc = (path: string): string =>
  `${import.meta.env.BASE_URL}${path}`;
